/**
 * Run Debug Drawer.
 *
 * Slide-up overlay (same motion pattern as {@link CacheDebugOverlay}) that
 * consolidates the workflow-run debug surfaces into one tabbed view:
 *
 *   - Overview    — diagnosis (slowest, failed, cache hotspots) + CLI pointers
 *   - Steps       — full waterfall
 *   - Cache       — per-step cache hit/miss (shared with CacheDebugOverlay)
 *   - Playwright  — viewer link + "what to look for" guide
 *   - Thoughts    — agent reasoning (filterable by step)
 *   - Telemetry   — superadmin: proxied logs + span tree
 *
 * URL-driven: `?debug=1&debug_tab=<id>&step=<stepId>`. All three search params
 * are preserved when switching tabs so deep-links stay shareable.
 *
 * @module FlowDesigner/runs/debug/RunDebugDrawer
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ArrowSquareOut,
  Copy,
  Check,
  Eye,
  Code,
  ChartBar,
  Warning,
  Lightning,
  ArrowDown,
  CheckCircle,
} from '@phosphor-icons/react';
import {
  Button,
  Text,
  Flex,
  UnderlineTabs,
  UnderlineTab,
  Dialog,
  DialogContent,
  DialogTrigger,
  LoadingOrbit,
} from '@chatsdet/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '../../../../../contexts/AuthContext';
import { CacheDebugContent } from '../cache-debug/CacheDebugContent';
import { RunWaterfallView } from '../RunWaterfallView';
import { StepThoughts } from '../StepThoughts';
import { useWorkflowRunThoughts } from '../../hooks/useWorkflowRunThoughts';
import type { WorkflowRunWithStepsDto } from '../../hooks/useWorkflowRun';
import type { WorkflowNode } from '../../../../../types/workflow';
import {
  useRunDebug,
  useRunNavigation,
  useRunTelemetryLogs,
  useRunTelemetryTrace,
  type RunDebugPayload,
} from './useRunDebug';
import { API_URL } from '../../../../../config';
import { flattenCredentialData, retentionLabel } from './run-debug-repro-ui';

export type DebugTabId =
  | 'overview'
  | 'steps'
  | 'navigation'
  | 'cache'
  | 'playwright'
  | 'thoughts'
  // Stays `sentry` while the tab renders telemetry: this value is the
  // `debug_tab` URL param, so renaming it breaks shared deep links.
  | 'sentry';

const DEFAULT_TAB: DebugTabId = 'overview';

type Props = {
  run: WorkflowRunWithStepsDto;
  nodes: WorkflowNode[];
};

type DebugSearch = {
  debug?: boolean;
  debug_tab?: DebugTabId;
  step?: string;
};

export function RunDebugDrawer({ run, nodes }: Props) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as DebugSearch;
  const isOpen = search.debug === true;
  const activeTab = (search.debug_tab as DebugTabId | undefined) ?? DEFAULT_TAB;
  const selectedStep = search.step ?? null;

  const setSearch = React.useCallback(
    (patch: Partial<DebugSearch> & { close?: boolean }) => {
      navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => {
          const next = { ...prev } as Record<string, unknown>;
          if (patch.close) {
            delete next.debug;
            delete next.debug_tab;
            delete next.step;
            return next;
          }
          if (patch.debug !== undefined) {
            if (patch.debug) next.debug = true;
            else delete next.debug;
          }
          if (patch.debug_tab !== undefined) {
            next.debug_tab = patch.debug_tab;
          }
          if ('step' in patch) {
            if (patch.step) next.step = patch.step;
            else delete next.step;
          }
          return next;
        },
        replace: false,
      });
    },
    [navigate]
  );

  const close = React.useCallback(() => setSearch({ close: true }), [setSearch]);
  const selectTab = React.useCallback(
    (id: DebugTabId) => setSearch({ debug_tab: id }),
    [setSearch]
  );
  const selectStep = React.useCallback(
    (stepId: string | null) => setSearch({ step: stepId ?? undefined }),
    [setSearch]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <RunDebugDrawerBody
          run={run}
          nodes={nodes}
          activeTab={activeTab}
          selectedStep={selectedStep}
          onClose={close}
          onSelectTab={selectTab}
          onSelectStep={selectStep}
        />
      )}
    </AnimatePresence>
  );
}

function RunDebugDrawerBody({
  run,
  nodes,
  activeTab,
  selectedStep,
  onClose,
  onSelectTab,
  onSelectStep,
}: {
  run: WorkflowRunWithStepsDto;
  nodes: WorkflowNode[];
  activeTab: DebugTabId;
  selectedStep: string | null;
  onClose: () => void;
  onSelectTab: (id: DebugTabId) => void;
  onSelectStep: (stepId: string | null) => void;
}) {
  const { user } = useAuth();
  const isSuperadmin = Boolean(user?.isSuperadmin);
  const debugQ = useRunDebug(run.workflowId, run.id, true);

  return (
    <>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] } }}
        className="fixed inset-0 z-[60] bg-background"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="fixed top-4 left-4 z-[70]"
      >
        <Button
          variant="ghost"
          size="sm"
          onPress={onClose}
          className="w-9 p-0"
          aria-label="Close debug"
        >
          <X size={20} weight="bold" />
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="fixed inset-0 z-[65] pointer-events-none flex justify-center"
      >
        <div className="w-full max-w-6xl h-full flex flex-col px-8 pt-14 pb-6 pointer-events-auto">
          <Flex align="center" justify="between" gap="md" className="shrink-0 pb-4">
            <Flex align="center" gap="sm">
              <Text weight="semibold" size="lg">
                Debug
              </Text>
              <Text size="sm" className="text-muted-foreground font-mono">
                {run.id.slice(0, 8)}
              </Text>
            </Flex>
            <CopyCliCommand command={`canary workflow debug ${run.id}`} />
          </Flex>

          <div className="flex-1 min-h-0 flex flex-col">
            <UnderlineTabs
              selectedId={activeTab}
              onSelectionChange={(id) => onSelectTab(id as DebugTabId)}
              layoutId="run-debug-tabs"
              className="shrink-0"
            >
              <UnderlineTab id="overview">Overview</UnderlineTab>
              <UnderlineTab id="steps">Steps</UnderlineTab>
              <UnderlineTab id="navigation">Navigation</UnderlineTab>
              <UnderlineTab id="cache">Cache</UnderlineTab>
              <UnderlineTab id="playwright">Playwright</UnderlineTab>
              <UnderlineTab id="thoughts">Thoughts</UnderlineTab>
              {isSuperadmin && <UnderlineTab id="sentry">Telemetry</UnderlineTab>}
            </UnderlineTabs>

            <div className="flex-1 min-h-0 pt-4 overflow-hidden">
              {activeTab === 'overview' && (
                <OverviewTab
                  data={debugQ.data}
                  isLoading={debugQ.isLoading}
                  error={debugQ.error ? String(debugQ.error) : null}
                  run={run}
                  runId={run.id}
                  onSelectTab={onSelectTab}
                  onSelectStep={onSelectStep}
                />
              )}
              {activeTab === 'steps' && (
                <StepsTab run={run} nodes={nodes} onSelectStep={onSelectStep} />
              )}
              {activeTab === 'navigation' && (
                <NavigationTab workflowId={run.workflowId} runId={run.id} />
              )}
              {activeTab === 'cache' && <CacheTab workflowId={run.workflowId} runId={run.id} />}
              {activeTab === 'playwright' && <PlaywrightTab data={debugQ.data} runId={run.id} />}
              {activeTab === 'thoughts' && (
                <ThoughtsTab
                  workflowId={run.workflowId}
                  runId={run.id}
                  selectedStep={selectedStep}
                  onSelectStep={onSelectStep}
                  steps={debugQ.data?.steps ?? []}
                />
              )}
              {activeTab === 'sentry' && isSuperadmin && (
                <TelemetryTab workflowId={run.workflowId} runId={run.id} selectedStep={selectedStep} />
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}

function CopyCliCommand({ command }: { command: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      onPress={async () => {
        await navigator.clipboard.writeText(command);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
      <code className="font-mono text-xs">{command}</code>
    </Button>
  );
}

function OverviewTab({
  data,
  isLoading,
  error,
  run: fullRun,
  runId,
  onSelectTab,
  onSelectStep,
}: {
  data: RunDebugPayload | undefined;
  isLoading: boolean;
  error: string | null;
  run: WorkflowRunWithStepsDto;
  runId: string;
  onSelectTab: (id: DebugTabId) => void;
  onSelectStep: (stepId: string | null) => void;
}) {
  const tokenTotals = React.useMemo(() => computeTokenTotals(fullRun), [fullRun]);

  // Baseline metrics derive from `fullRun` (already in memory) so Overview renders
  // immediately, even if the /debug aggregator is slow, errored, or not yet deployed.
  const runStart = fullRun.startedAt ? new Date(fullRun.startedAt).getTime() : null;
  const runEnd = fullRun.finishedAt ? new Date(fullRun.finishedAt).getTime() : null;
  const baselineDurationMs =
    runStart !== null && runEnd !== null ? Math.max(0, runEnd - runStart) : null;
  const durationSec =
    data && data.run.durationMs !== null
      ? (data.run.durationMs / 1000).toFixed(1)
      : baselineDurationMs !== null
        ? (baselineDurationMs / 1000).toFixed(1)
        : '—';
  const status = data?.run.status ?? fullRun.status;
  const hitPct = data ? Math.round(data.cache.hitRate * 100) : null;

  const statusTone = getStatusTone(status);
  const totalTokens = tokenTotals.totalInput + tokenTotals.totalOutput;
  const maxStepTokens = Math.max(1, ...tokenTotals.perStep.map((r) => r.input + r.output));
  const hasDiagnosis =
    data &&
    (data.diagnosis.failedSteps.length > 0 ||
      data.diagnosis.slowestSteps.length > 0 ||
      data.diagnosis.cacheMissHotspots.length > 0);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-8 pr-2 pb-8">
        {/* Hero row: status + primary metrics */}
        <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatusMetric status={status} tone={statusTone} />
          <BigMetric label="Duration" value={`${durationSec}s`} />
          <BigMetric
            label="Cache hit rate"
            value={hitPct !== null ? `${hitPct}%` : '—'}
            sub={
              data
                ? `${data.cache.hitCount} of ${data.cache.hitCount + data.cache.missCount} steps`
                : !error && isLoading
                  ? 'loading'
                  : 'unavailable'
            }
            accent={
              hitPct !== null
                ? hitPct >= 80
                  ? 'positive'
                  : hitPct >= 40
                    ? 'neutral'
                    : 'warning'
                : 'neutral'
            }
          />
          <BigMetric
            label="Agent thoughts"
            value={data ? String(data.thoughts.count) : '—'}
            sub={data ? 'reasoning events' : !error && isLoading ? 'loading' : 'unavailable'}
          />
        </section>

        {data?.repro?.sandbox && <ManualReproCard repro={data.repro} />}

        {/* Tokens row */}
        {tokenTotals.hasData && (
          <section className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <BigMetric
              label="Total tokens"
              value={compactNumber(totalTokens)}
              sub={`${compactNumber(tokenTotals.totalInput)} in · ${compactNumber(tokenTotals.totalOutput)} out`}
            />
            {tokenTotals.totalCached > 0 && (
              <BigMetric
                label="Cached tokens"
                value={compactNumber(tokenTotals.totalCached)}
                sub={`${Math.round((tokenTotals.totalCached / Math.max(1, tokenTotals.totalInput)) * 100)}% of input`}
                accent="positive"
              />
            )}
            {tokenTotals.contextPct !== null && (
              <BigMetric
                label="Peak context"
                value={`${tokenTotals.contextPct}%`}
                sub={`${compactNumber(tokenTotals.peakInput)} / ${compactNumber(tokenTotals.contextWindow!)}`}
                accent={
                  tokenTotals.contextPct >= 85
                    ? 'warning'
                    : tokenTotals.contextPct >= 60
                      ? 'neutral'
                      : 'positive'
                }
              />
            )}
          </section>
        )}

        {error && !data && (
          <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 dark:border-amber-700/60 dark:bg-amber-900/10 p-4">
            <Flex gap="sm" align="start">
              <Warning
                size={18}
                weight="duotone"
                className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
              />
              <div>
                <Text size="sm" weight="medium" className="text-amber-900 dark:text-amber-100">
                  Diagnosis unavailable
                </Text>
                <Text size="xs" className="text-amber-800 dark:text-amber-300">
                  The debug aggregator returned an error. Per-tab views (Steps, Cache, Thoughts,
                  Playwright) still work — open them directly.
                </Text>
              </div>
            </Flex>
          </div>
        )}

        {/* Diagnosis */}
        {data && !hasDiagnosis && (
          <div className="rounded-lg border border-border bg-card p-6">
            <Flex gap="sm" align="center">
              <CheckCircle
                size={20}
                weight="duotone"
                className="text-emerald-600 dark:text-emerald-400"
              />
              <div>
                <Text weight="medium">No obvious issues</Text>
                <Text size="sm" className="text-muted-foreground">
                  Nothing failed, nothing stood out as slow, no cache hotspots. Drill into a tab if
                  you're chasing a subtle regression.
                </Text>
              </div>
            </Flex>
          </div>
        )}

        {data && data.diagnosis.failedSteps.length > 0 && (
          <DiagnosisGroup
            icon={<Warning size={16} weight="duotone" />}
            tone="destructive"
            title="Failed steps"
            items={data.diagnosis.failedSteps.map((s) => ({
              key: s.stepId,
              title: s.name ?? s.stepId.slice(0, 8),
              detail: s.errorMessage ?? '',
              onInspect: () => {
                onSelectStep(s.stepId);
                onSelectTab('thoughts');
              },
            }))}
          />
        )}

        {data && data.diagnosis.slowestSteps.length > 0 && (
          <DiagnosisGroup
            icon={<Lightning size={16} weight="duotone" />}
            tone="warning"
            title="Slowest steps"
            items={data.diagnosis.slowestSteps.map((s) => ({
              key: s.stepId,
              title: s.name ?? s.stepId.slice(0, 8),
              detail: `${(s.durationMs / 1000).toFixed(2)}s`,
              onInspect: () => {
                onSelectStep(s.stepId);
                onSelectTab('steps');
              },
            }))}
          />
        )}

        {data && data.diagnosis.cacheMissHotspots.length > 0 && (
          <DiagnosisGroup
            icon={<ArrowDown size={16} weight="duotone" />}
            tone="neutral"
            title="Cache miss hotspots"
            items={data.diagnosis.cacheMissHotspots.map((s) => ({
              key: s.stepId,
              title: s.stepId.slice(0, 8),
              detail: s.reason,
              onInspect: () => {
                onSelectStep(s.stepId);
                onSelectTab('cache');
              },
            }))}
          />
        )}

        {/* Tokens per step */}
        {tokenTotals.hasData && tokenTotals.perStep.length > 0 && (
          <section>
            <SectionHeader title="Token usage" />
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-[1fr_80px_80px_80px] gap-3 bg-muted/30 px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                <div>Step</div>
                <div className="text-right">In</div>
                <div className="text-right">Out</div>
                <div className="text-right">Cached</div>
              </div>
              {tokenTotals.perStep.map((row, idx) => {
                const step = fullRun.steps.find((s) => s.id === row.stepId);
                const name = step
                  ? (((step.metadataJson as Record<string, unknown> | null)?.nodeName as string) ??
                    row.stepId.slice(0, 8))
                  : row.stepId.slice(0, 8);
                const pct = ((row.input + row.output) / maxStepTokens) * 100;
                return (
                  <div
                    key={row.stepId}
                    className={`relative grid grid-cols-[1fr_80px_80px_80px] gap-3 px-4 py-2 text-sm ${
                      idx > 0 ? 'border-t border-border' : ''
                    }`}
                  >
                    <div
                      aria-hidden
                      className="absolute inset-y-0 left-0 bg-primary/5"
                      style={{ width: `${pct}%` }}
                    />
                    <div className="relative truncate">{name}</div>
                    <div className="relative text-right font-mono tabular-nums text-muted-foreground">
                      {compactNumber(row.input)}
                    </div>
                    <div className="relative text-right font-mono tabular-nums text-muted-foreground">
                      {compactNumber(row.output)}
                    </div>
                    <div className="relative text-right font-mono tabular-nums text-muted-foreground">
                      {row.cached > 0 ? compactNumber(row.cached) : '—'}
                    </div>
                  </div>
                );
              })}
              <div className="grid grid-cols-[1fr_80px_80px_80px] gap-3 border-t border-border bg-muted/20 px-4 py-2 text-sm font-medium">
                <div>Total</div>
                <div className="text-right font-mono tabular-nums">
                  {tokenTotals.totalInput.toLocaleString()}
                </div>
                <div className="text-right font-mono tabular-nums">
                  {tokenTotals.totalOutput.toLocaleString()}
                </div>
                <div className="text-right font-mono tabular-nums">
                  {tokenTotals.totalCached > 0 ? tokenTotals.totalCached.toLocaleString() : '—'}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* How to debug — three command cards */}
        <section>
          <SectionHeader
            title="How to debug"
            subtitle="Triangulate three signals. Each has an in-app tab and a CLI equivalent."
          />
          <div className="grid gap-3 md:grid-cols-3">
            <HowToCard
              icon={<Eye size={18} weight="duotone" />}
              title="Visual"
              body="See what happened on screen — Playwright trace with DOM snapshots and network."
              command={`canary workflow debug playwright ${runId}`}
            />
            <HowToCard
              icon={<Code size={18} weight="duotone" />}
              title="DOM"
              body="Reconstruct what the agent saw at each step — semantic snapshots from the trace."
              command={`canary workflow debug trace-snapshot ${runId}`}
            />
            <HowToCard
              icon={<ChartBar size={18} weight="duotone" />}
              title="Telemetry"
              body="Read the timeline — step timings, cache hits, agent reasoning, logs, spans."
              command={`canary workflow debug steps --slow ${runId}`}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

type ReproPayload = NonNullable<RunDebugPayload['repro']>;
type ReproCredential = ReproPayload['credentials'][number];

type RevealedCredential = {
  name: string;
  data: Record<string, unknown>;
  loginUrl: string | null;
  loginInstructions: string | null;
};

function ManualReproCard({ repro }: { repro: ReproPayload }) {
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const sandbox = repro.sandbox;
  if (!sandbox) return null;
  const countdown = retentionLabel(sandbox.expiresAt, nowMs);

  return (
    <section className="rounded-lg border border-amber-300/70 bg-amber-50/50 p-5 dark:border-amber-700/60 dark:bg-amber-950/15">
      <Flex justify="between" align="start" gap="md">
        <div className="space-y-1">
          <Text weight="semibold">Manual repro</Text>
          <Text size="sm" className="text-muted-foreground">
            {repro.available
              ? `This failed run's sandbox is retained · ${countdown}`
              : `Sandbox unavailable · ${repro.unavailableReason ?? sandbox.status}`}
          </Text>
        </div>
        {repro.available && sandbox.publicUrl ? (
          <a href={sandbox.publicUrl} target="_blank" rel="noreferrer">
            <Button size="sm" variant="secondary" iconRight={<ArrowSquareOut size={14} />}>
              Open sandbox
            </Button>
          </a>
        ) : null}
      </Flex>

      {repro.available ? (
        repro.credentials.length > 0 ? (
          <div className="mt-4 space-y-2">
            {repro.credentials.map((credential) => (
              <div
                key={credential.id}
                className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-background/70 px-3 py-2"
              >
                <div className="min-w-0">
                  <Text size="sm" weight="medium" className="truncate">
                    {credential.name}
                  </Text>
                  <Text size="xs" className="text-muted-foreground font-mono truncate">
                    {credential.role ? `${credential.role} · ` : ''}
                    {credential.id}
                  </Text>
                </div>
                <CredentialRevealTrigger credential={credential} />
              </div>
            ))}
          </div>
        ) : (
          <Text size="sm" className="mt-4 text-muted-foreground">
            No ephemeral login credentials were discovered for this sandbox.
          </Text>
        )
      ) : null}
    </section>
  );
}

function CredentialRevealTrigger({ credential }: { credential: ReproCredential }) {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="lg">
      <Button size="sm" variant="ghost">
        Reveal login
      </Button>
      <CredentialRevealDialog credential={credential} isOpen={isOpen} />
    </DialogTrigger>
  );
}

function CredentialRevealDialog({
  credential,
  isOpen,
}: {
  credential: ReproCredential;
  isOpen: boolean;
}) {
  const [revealed, setRevealed] = React.useState<RevealedCredential | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      setRevealed(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    fetch(
      `${API_URL}/org/properties/${encodeURIComponent(credential.propertyId)}/credentials/${encodeURIComponent(credential.id)}`,
      { credentials: 'include', cache: 'no-store', signal: controller.signal }
    )
      .then(async (response) => {
        if (!response.ok) throw new Error('Failed to load credential');
        const body = (await response.json()) as { credential?: RevealedCredential };
        if (!body.credential) throw new Error('Credential details unavailable');
        if (controller.signal.aborted) return;
        setRevealed(body.credential);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : 'Failed to load credential');
      });
    return () => {
      controller.abort();
      setRevealed(null);
      setError(null);
    };
  }, [credential.id, credential.propertyId, isOpen]);

  const rows = revealed ? flattenCredentialData(revealed.data) : [];

  return (
    <Dialog
      title={`Login · ${credential.name}`}
      description="Decrypted only while this dialog is open"
    >
      <DialogContent className="space-y-4">
        {!revealed && !error ? (
          <Flex justify="center" className="py-8">
            <LoadingOrbit size="sm" />
          </Flex>
        ) : error ? (
          <Text size="sm" className="text-destructive">
            {error}
          </Text>
        ) : (
          <div className="space-y-3">
            {revealed?.loginUrl ? (
              <SecretField label="Login URL" value={revealed.loginUrl} link />
            ) : null}
            {rows.map((row) => (
              <SecretField key={row.label} label={row.label} value={row.value} />
            ))}
            {revealed?.loginInstructions ? (
              <div className="space-y-1">
                <Text size="xs" className="uppercase tracking-wide text-muted-foreground">
                  Instructions
                </Text>
                <Text size="sm" className="whitespace-pre-wrap">
                  {revealed.loginInstructions}
                </Text>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SecretField({
  label,
  value,
  link = false,
}: {
  label: string;
  value: string;
  link?: boolean;
}) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
      <Flex justify="between" align="center" gap="sm">
        <div className="min-w-0">
          <Text size="xs" className="uppercase tracking-wide text-muted-foreground">
            {label}
          </Text>
          {link ? (
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              className="block break-all text-sm underline"
            >
              {value}
            </a>
          ) : (
            <Text size="sm" className="break-all font-mono">
              {value}
            </Text>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Copy ${label}`}
          onPress={copy}
          iconLeft={copied ? <Check size={14} /> : <Copy size={14} />}
        />
      </Flex>
    </div>
  );
}

type Tone = 'positive' | 'neutral' | 'warning' | 'destructive';

function getStatusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (s === 'success' || s === 'succeeded' || s === 'completed') return 'positive';
  if (s === 'failed' || s === 'error') return 'destructive';
  if (s === 'running' || s === 'waiting' || s === 'queued') return 'neutral';
  if (s === 'canceled' || s === 'cancelled' || s === 'skipped') return 'warning';
  return 'neutral';
}

const toneClasses: Record<Tone, { dot: string; value: string; chip: string }> = {
  positive: {
    dot: 'bg-emerald-500',
    value: 'text-emerald-700 dark:text-emerald-300',
    chip: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
  },
  neutral: {
    dot: 'bg-blue-500',
    value: 'text-foreground',
    chip: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
  },
  warning: {
    dot: 'bg-amber-500',
    value: 'text-amber-700 dark:text-amber-300',
    chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
  },
  destructive: {
    dot: 'bg-orange-500',
    value: 'text-orange-700 dark:text-orange-300',
    chip: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20',
  },
};

function StatusMetric({ status, tone }: { status: string; tone: Tone }) {
  const c = toneClasses[tone];
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <Text size="xs" className="text-muted-foreground uppercase tracking-wide font-medium">
        Status
      </Text>
      <Flex align="center" gap="sm" className="mt-2">
        <span className={`inline-block h-2 w-2 rounded-full ${c.dot}`} />
        <Text size="xl" weight="semibold" className={`capitalize ${c.value}`}>
          {status}
        </Text>
      </Flex>
    </div>
  );
}

function BigMetric({
  label,
  value,
  sub,
  accent = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: Tone;
}) {
  const c = toneClasses[accent];
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <Text size="xs" className="text-muted-foreground uppercase tracking-wide font-medium">
        {label}
      </Text>
      <Text
        size="xl"
        weight="semibold"
        className={`mt-2 tabular-nums ${accent === 'neutral' ? 'text-foreground' : c.value}`}
      >
        {value}
      </Text>
      {sub && (
        <Text size="xs" className="text-muted-foreground mt-0.5">
          {sub}
        </Text>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-3">
      <Text weight="semibold">{title}</Text>
      {subtitle && (
        <Text size="sm" className="text-muted-foreground">
          {subtitle}
        </Text>
      )}
    </div>
  );
}

function DiagnosisGroup({
  icon,
  tone,
  title,
  items,
}: {
  icon: React.ReactNode;
  tone: Tone;
  title: string;
  items: Array<{ key: string; title: string; detail: string; onInspect: () => void }>;
}) {
  const c = toneClasses[tone];
  return (
    <section>
      <Flex align="center" gap="sm" className="mb-2">
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${c.chip}`}
        >
          {icon}
        </span>
        <Text weight="semibold">{title}</Text>
        <Text size="xs" className="text-muted-foreground">
          {items.length}
        </Text>
      </Flex>
      <div className="rounded-lg border border-border bg-card divide-y divide-border">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={item.onInspect}
            className="group flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="min-w-0">
              <Text size="sm" weight="medium" className="truncate">
                {item.title}
              </Text>
              {item.detail && (
                <Text size="xs" className="text-muted-foreground truncate">
                  {item.detail}
                </Text>
              )}
            </div>
            <ArrowSquareOut
              size={14}
              className="shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
            />
          </button>
        ))}
      </div>
    </section>
  );
}

function HowToCard({
  icon,
  title,
  body,
  command,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  command: string;
}) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-2">
      <Flex align="center" gap="sm">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
          {icon}
        </span>
        <Text weight="semibold">{title}</Text>
      </Flex>
      <Text size="sm" className="text-muted-foreground flex-1">
        {body}
      </Text>
      <button
        type="button"
        onClick={async () => {
          await navigator.clipboard.writeText(command);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="group mt-1 flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-left font-mono text-xs transition-colors hover:bg-muted/60"
      >
        <span className="truncate">{command}</span>
        {copied ? (
          <Check size={14} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <Copy
            size={14}
            className="shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
          />
        )}
      </button>
    </div>
  );
}

function compactNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

type TokenTotals = {
  hasData: boolean;
  totalInput: number;
  totalOutput: number;
  totalCached: number;
  peakInput: number;
  contextWindow: number | null;
  contextPct: number | null;
  perStep: Array<{ stepId: string; nodeId: string; input: number; output: number; cached: number }>;
};

function computeTokenTotals(run: WorkflowRunWithStepsDto): TokenTotals {
  let totalInput = 0;
  let totalOutput = 0;
  let totalCached = 0;
  let peakInput = 0;
  let contextWindow: number | null = null;
  let hasData = false;
  const perStep: TokenTotals['perStep'] = [];
  for (const step of run.steps) {
    const meta = (step.metadataJson ?? {}) as Record<string, unknown>;
    const usage = meta.tokenUsage as
      | {
          inputTokens?: number;
          outputTokens?: number;
          cacheReadInputTokens?: number;
          maxInputTokens?: number;
        }
      | undefined;
    if (!usage) continue;
    hasData = true;
    const i = usage.inputTokens ?? 0;
    const o = usage.outputTokens ?? 0;
    const c = usage.cacheReadInputTokens ?? 0;
    totalInput += i;
    totalOutput += o;
    totalCached += c;
    if (usage.maxInputTokens && usage.maxInputTokens > peakInput) peakInput = usage.maxInputTokens;
    if (contextWindow === null && typeof meta.contextWindowTokens === 'number') {
      contextWindow = meta.contextWindowTokens as number;
    }
    perStep.push({ stepId: step.id, nodeId: step.nodeId, input: i, output: o, cached: c });
  }
  const contextPct =
    contextWindow && peakInput ? Math.round((peakInput / contextWindow) * 100) : null;
  return {
    hasData,
    totalInput,
    totalOutput,
    totalCached,
    peakInput,
    contextWindow,
    contextPct,
    perStep,
  };
}

function StepsTab({
  run,
  nodes,
  onSelectStep,
}: {
  run: WorkflowRunWithStepsDto;
  nodes: WorkflowNode[];
  onSelectStep: (stepId: string | null) => void;
}) {
  const { data: thoughts } = useWorkflowRunThoughts(run.workflowId, run.id);
  return (
    <div className="h-full overflow-y-auto">
      <RunWaterfallView
        run={run}
        thoughts={thoughts ?? null}
        nodes={nodes}
        onStepSelect={(stepId) => onSelectStep(stepId)}
      />
    </div>
  );
}

function CacheTab({ workflowId, runId }: { workflowId: string; runId: string }) {
  return (
    <Flex direction="column" gap="sm" className="h-full min-h-0">
      <details className="rounded-md border border-border bg-card px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">How to read cache outcomes</summary>
        <div className="text-xs text-muted-foreground mt-2 space-y-1">
          <p>
            <b>replay</b> — cached action ran deterministically (fastest).
          </p>
          <p>
            <b>recovery</b> — agent healed a stale selector at L2.
          </p>
          <p>
            <b>l2_gap</b> — agent filled a gap between cached actions.
          </p>
          <p>
            <b>wait_shortcut</b> — wait condition satisfied via cached signal.
          </p>
          <p>
            <b>null</b> — no cache; agent ran this step from scratch. See miss reason for why.
          </p>
        </div>
      </details>
      <div className="flex-1 min-h-0">
        <CacheDebugContent workflowId={workflowId} runId={runId} enabled />
      </div>
    </Flex>
  );
}

function NavigationTab({ workflowId, runId }: { workflowId: string; runId: string }) {
  const navQ = useRunNavigation(workflowId, runId);

  if (navQ.isLoading) {
    return <Text className="text-muted-foreground">Loading navigation history…</Text>;
  }
  if (navQ.error) {
    return <Text className="text-warning">Failed to load navigation: {String(navQ.error)}</Text>;
  }
  const data = navQ.data;
  if (!data || data.entries.length === 0) {
    return (
      <Flex direction="column" gap="sm">
        <Text className="text-muted-foreground">No navigation events recorded for this run.</Text>
        <Text size="xs" className="text-muted-foreground">
          Runs from before navigation tracking shipped won't show data here. Re-run the flow to
          populate it.
        </Text>
      </Flex>
    );
  }

  const transitionAtIndex = new Map(data.transitions.map((t) => [t.toIndex, t] as const));

  return (
    <Flex direction="column" gap="md" className="h-full min-h-0 overflow-auto">
      <details className="rounded-md border border-border bg-card px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">How to read navigation</summary>
        <div className="text-xs text-muted-foreground mt-2 space-y-1">
          <p>
            Each row is a navigation event the browser observed — full page load, redirect, or SPA
            pushState/popstate. Timestamps are relative to the run start.
          </p>
          <p>
            Rows highlighted in orange crossed to a different hostname from the previous entry. When
            an auth/permission failure follows a cross-host transition, that's almost always the
            root cause — not stale credentials.
          </p>
        </div>
      </details>

      {data.transitions.length > 0 && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
          <Text size="sm" weight="medium">
            {data.transitions.length} cross-host transition
            {data.transitions.length === 1 ? '' : 's'} during this run.
          </Text>
          <Text size="xs" className="text-muted-foreground mt-1">
            Check these first when diagnosing auth/permission failures.
          </Text>
        </div>
      )}

      <table className="w-full text-xs border-collapse">
        <thead className="text-muted-foreground">
          <tr className="border-b border-border">
            <th className="text-left py-1 pr-2">Time</th>
            <th className="text-left py-1 pr-2">Hostname</th>
            <th className="text-left py-1 pr-2">URL</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.map((entry, idx) => {
            const transition = transitionAtIndex.get(idx);
            return (
              <tr
                key={`${entry.timestampMs}-${idx}`}
                className={
                  transition ? 'border-b border-border bg-warning/10' : 'border-b border-border'
                }
              >
                <td className="py-1 pr-2 font-mono whitespace-nowrap">
                  {formatRelMs(entry.timestampMs)}
                </td>
                <td className="py-1 pr-2 font-mono">
                  {entry.hostname ?? '—'}
                  {transition && (
                    <span className="ml-2 rounded bg-warning/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                      from {transition.fromHostname}
                    </span>
                  )}
                </td>
                <td className="py-1 pr-2 font-mono text-muted-foreground break-all">{entry.url}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Flex>
  );
}

function formatRelMs(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const millis = ms % 1000;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

function PlaywrightTab({ data, runId }: { data: RunDebugPayload | undefined; runId: string }) {
  const viewerUrl = data?.playwright.viewerUrl ?? null;
  return (
    <Flex direction="column" gap="md" className="h-full min-h-0">
      {viewerUrl ? (
        <>
          <Flex gap="sm">
            <Button
              variant="outline"
              size="sm"
              onPress={() => window.open(viewerUrl, '_blank', 'noopener')}
            >
              <ArrowSquareOut size={14} className="mr-1" /> Open trace viewer
            </Button>
            <CopyCliCommand command={`canary workflow debug trace-snapshot ${runId}`} />
          </Flex>
          <iframe
            title="Playwright trace"
            src={viewerUrl}
            className="flex-1 min-h-0 w-full rounded-md border border-border bg-card"
          />
        </>
      ) : (
        <Text className="text-muted-foreground">
          No Playwright trace was captured for this run.
        </Text>
      )}
      <Text size="xs" className="text-muted-foreground">
        The embedded viewer shows each action, the DOM snapshot, and network activity. For DOM-only
        snapshots (what the agent saw), run the CLI command above.
      </Text>
    </Flex>
  );
}

function ThoughtsTab({
  workflowId,
  runId,
  selectedStep,
  onSelectStep,
  steps,
}: {
  workflowId: string;
  runId: string;
  selectedStep: string | null;
  onSelectStep: (stepId: string | null) => void;
  steps: RunDebugPayload['steps'];
}) {
  const { data } = useWorkflowRunThoughts(workflowId, runId);
  const thoughts = data ?? [];
  const filtered = selectedStep ? thoughts.filter((t) => t.stepId === selectedStep) : thoughts;

  return (
    <Flex direction="column" gap="sm" className="h-full min-h-0">
      <Flex gap="sm" wrap align="center">
        <Text size="sm" className="text-muted-foreground">
          Filter:
        </Text>
        <Button
          size="sm"
          variant={selectedStep === null ? 'outline' : 'ghost'}
          onPress={() => onSelectStep(null)}
        >
          All ({thoughts.length})
        </Button>
        {steps.map((s) => {
          const count = thoughts.filter((t) => t.stepId === s.id).length;
          if (count === 0) return null;
          return (
            <Button
              key={s.id}
              size="sm"
              variant={selectedStep === s.id ? 'outline' : 'ghost'}
              onPress={() => onSelectStep(s.id)}
            >
              {s.name ?? s.id.slice(0, 8)} ({count})
            </Button>
          );
        })}
      </Flex>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <StepThoughts thoughts={filtered} defaultExpanded />
      </div>
    </Flex>
  );
}

function TelemetryTab({
  workflowId,
  runId,
  selectedStep,
}: {
  workflowId: string;
  runId: string;
  selectedStep: string | null;
}) {
  const [level, setLevel] = React.useState<string | undefined>(undefined);
  const logsQ = useRunTelemetryLogs(workflowId, runId, {
    enabled: true,
    level,
    stepId: selectedStep ?? undefined,
  });
  const traceQ = useRunTelemetryTrace(workflowId, runId, {
    enabled: true,
    stepId: selectedStep ?? undefined,
  });

  return (
    <Flex direction="column" gap="md" className="h-full min-h-0 overflow-y-auto pr-2">
      <Flex gap="sm" align="center">
        <Text size="sm" weight="semibold">
          Logs
        </Text>
        {(['info', 'warning', 'error'] as const).map((lv) => (
          <Button
            key={lv}
            size="sm"
            variant={level === lv ? 'outline' : 'ghost'}
            onPress={() => setLevel(level === lv ? undefined : lv)}
          >
            {lv}
          </Button>
        ))}
        <Button
          size="sm"
          variant="ghost"
          onPress={() =>
            window.open(
              `https://tstc-9n.sentry.io/explore/logs/?logsQuery=${encodeURIComponent(`annotations.runId:${runId}`)}`,
              '_blank',
              'noopener'
            )
          }
        >
          <ArrowSquareOut size={14} className="mr-1" /> Open raw
        </Button>
      </Flex>

      {logsQ.data?.partial && (
        <Text size="xs" className="text-amber-600 dark:text-amber-400">
          Partial result: {logsQ.data.reason}
        </Text>
      )}

      <div className="rounded-md border border-border bg-card">
        {logsQ.isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading logs…</div>
        ) : (logsQ.data?.rows ?? []).length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No logs.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Level</th>
                <th className="px-3 py-2">Message</th>
              </tr>
            </thead>
            <tbody>
              {(logsQ.data?.rows ?? []).map((row, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                    {row.timestamp.slice(11, 19)}
                  </td>
                  <td className="px-3 py-1.5">
                    <LogLevelPill level={row.level} />
                  </td>
                  <td className="px-3 py-1.5">{row.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Text size="sm" weight="semibold">
        Trace spans
      </Text>
      {traceQ.data?.partial && (
        <Text size="xs" className="text-amber-600 dark:text-amber-400">
          Partial result: {traceQ.data.reason}
        </Text>
      )}
      <div className="rounded-md border border-border bg-card">
        {traceQ.isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading spans…</div>
        ) : (traceQ.data?.spans ?? []).length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No spans.</div>
        ) : (
          <ul className="p-3 space-y-1 text-xs font-mono">
            {(traceQ.data?.spans ?? []).map((span) => (
              <li key={span.spanId}>
                <span className="text-muted-foreground">{span.op ?? 'span'}</span>{' '}
                <span>{span.description ?? ''}</span>{' '}
                <span className="text-muted-foreground">
                  {span.durationMs !== null ? `${span.durationMs}ms` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Flex>
  );
}

function LogLevelPill({ level }: { level: string }) {
  const color =
    level === 'error' || level === 'fatal'
      ? 'text-amber-700 bg-amber-100 dark:bg-amber-900/30'
      : level === 'warning' || level === 'warn'
        ? 'text-amber-700 bg-amber-50 dark:bg-amber-900/20'
        : 'text-blue-700 bg-blue-50 dark:bg-blue-900/20';
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${color}`}>
      {level}
    </span>
  );
}
